/**
 * Accounting Friendly Layer API service
 */
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../../../shared/api/axiosInstance';
import type { DocumentDistributePayload, DocumentDistributionItem } from '../../../shared/documents';
import type {
  Invoice, InvoiceCreate, InvoiceLineCreate, InvoiceUpdate, ListInvoicesParams,
  BulkInvoiceActionResult,
  RevenueRecognitionSchedule, DeferredRevenueRollforward,
  Payment, PaymentCreate, PaymentAllocationCreate, ListPaymentsParams, InvoicePayment,
  Bill, BillCreate, BillUpdate, BillShipmentLink, ListBillsParams,
  UnbilledReceiptSummary,
  LandedCostCategory,
  TaxRate, TaxRateCreate, TaxRateUpdate, ListTaxRatesParams,
  TaxDeterminationRule, TaxDeterminationRuleCreate, TaxDeterminationRuleUpdate,
  ListTaxDeterminationRulesParams, TaxDeterminationResolveRequest, TaxDeterminationResult,
  BankAccount, BankAccountCreateRequest, BankAccountUpdateRequest, ListBankAccountsParams,
  RecurringTemplate, CreateRecurringFromInvoice, CreateRecurringFromBill, UpdateRecurring, ListRecurringParams,
  PaginatedResponse, DataResponse,
  BankDeposit, BankDepositCreate, BankDepositUpdate,
  BankDepositLineCreate, BankDepositValidateResult, BankDepositPostRequest,
  BankDepositVoidRequest, ListBankDepositsParams,
  PendingReceipt, ListPendingReceiptsParams,
  BankTransfer, BankTransferCreate, BankTransferVoidRequest, ListBankTransfersParams,
  BankFeePostRequest, ConfirmDepositMatchRequest, DepositRoutingDefault,
  PayerBalance, PayerReimbursementLedgerEntry, PayerTransactionsResponse,
  PayerEntityTable, ReimbursementClearRequest, ReimbursementClearBatchRequest,
  ListPayerBalancesParams, ListPayerTransactionsParams,
} from '../types';

const API_BASE = '/api/v1/accounting';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildParams(params?: Record<string, any>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      sp.set(key, String(value));
    }
  }
  const str = sp.toString();
  return str ? `?${str}` : '';
}

// ============== Invoices ==============

export const invoiceApi = {
  list: async (params?: ListInvoicesParams): Promise<PaginatedResponse<Invoice>> =>
    apiGet<PaginatedResponse<Invoice>>(`${API_BASE}/invoices${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<Invoice>> =>
    apiGet<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}`),

  create: async (data: InvoiceCreate): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices`, data),

  update: async (id: string, data: InvoiceUpdate): Promise<DataResponse<Invoice>> =>
    apiPut<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/invoices/${id}`);
  },

  send: async (id: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/send`),

  void: async (id: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/void`),

  // Bulk send/void: bounded (≤100 ids) batch actions returning per-id succeeded /
  // skipped{ id, reason } so the caller can report partial results.
  bulkSend: async (invoiceIds: string[]): Promise<DataResponse<BulkInvoiceActionResult>> =>
    apiPost<DataResponse<BulkInvoiceActionResult>>(`${API_BASE}/invoices/bulk-send`, {
      invoice_ids: invoiceIds,
    }),

  bulkVoid: async (invoiceIds: string[]): Promise<DataResponse<BulkInvoiceActionResult>> =>
    apiPost<DataResponse<BulkInvoiceActionResult>>(`${API_BASE}/invoices/bulk-void`, {
      invoice_ids: invoiceIds,
    }),

  // Write off an uncollectible invoice: posts DR Bad Debt / CR AR and flips the
  // invoice to written_off (relieving AR aging + dunning). Requires a reason.
  writeOff: async (id: string, reason: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/write-off`, { reason }),

  clone: async (id: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/clone`),

  createCreditNote: async (
    id: string,
    options?: { lines?: InvoiceLineCreate[]; reason?: string },
  ): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/credit-note`, {
      ...(options?.lines ? { lines: options.lines } : {}),
      ...(options?.reason ? { reason: options.reason } : {}),
    }),

  // Deferred-revenue [33]: the recognition schedule rows for this invoice.
  revenueRecognitionSchedule: async (id: string): Promise<DataResponse<RevenueRecognitionSchedule[]>> =>
    apiGet<DataResponse<RevenueRecognitionSchedule[]>>(
      `${API_BASE}/invoices/${id}/revenue-recognition-schedule`,
    ),
};

// ============== Deferred-revenue reports ==============

export const deferredRevenueApi = {
  rollforward: async (
    startDate: string,
    endDate: string,
  ): Promise<DataResponse<DeferredRevenueRollforward>> =>
    apiGet<DataResponse<DeferredRevenueRollforward>>(
      `${API_BASE}/reports/deferred-revenue-rollforward?start_date=${startDate}&end_date=${endDate}`,
    ),
};

// ============== Payments ==============

export const paymentApi = {
  list: async (params?: ListPaymentsParams): Promise<PaginatedResponse<Payment>> =>
    apiGet<PaginatedResponse<Payment>>(`${API_BASE}/payments${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<Payment>> =>
    apiGet<DataResponse<Payment>>(`${API_BASE}/payments/${id}`),

  listForInvoice: async (invoiceId: string): Promise<DataResponse<InvoicePayment[]>> =>
    apiGet<DataResponse<InvoicePayment[]>>(`${API_BASE}/invoices/${invoiceId}/payments`),

  create: async (data: PaymentCreate): Promise<DataResponse<Payment>> =>
    apiPost<DataResponse<Payment>>(`${API_BASE}/payments`, data),

  void: async (id: string): Promise<DataResponse<Payment>> =>
    apiPost<DataResponse<Payment>>(`${API_BASE}/payments/${id}/void`),

  // Apply an existing vendor (made) payment to open bills after creation.
  allocate: async (
    id: string,
    allocations: PaymentAllocationCreate[],
  ): Promise<DataResponse<Payment>> =>
    apiPost<DataResponse<Payment>>(`${API_BASE}/payments/${id}/allocations`, { allocations }),

  distribute: async (
    id: string,
    payload: DocumentDistributePayload,
  ): Promise<DataResponse<DocumentDistributionItem>> =>
    apiPost<DataResponse<DocumentDistributionItem>>(`${API_BASE}/payments/${id}/distribute`, payload),
};

// ============== Bills ==============

export const billApi = {
  list: async (params?: ListBillsParams): Promise<PaginatedResponse<Bill>> =>
    apiGet<PaginatedResponse<Bill>>(`${API_BASE}/bills${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<Bill>> =>
    apiGet<DataResponse<Bill>>(`${API_BASE}/bills/${id}`),

  create: async (data: BillCreate): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills`, data),

  update: async (id: string, data: BillUpdate): Promise<DataResponse<Bill>> =>
    apiPut<DataResponse<Bill>>(`${API_BASE}/bills/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/bills/${id}`);
  },

  submit: async (id: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/${id}/submit`),

  approve: async (id: string, acceptVariance = false): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(
      `${API_BASE}/bills/${id}/approve`,
      // accept_variance is the explicit reviewer decision to book an
      // out-of-tolerance three-way-match variance (BILL_THREE_WAY_MATCH_HOLD).
      acceptVariance ? { accept_variance: true } : undefined
    ),

  void: async (id: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/${id}/void`),

  clone: async (id: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/${id}/clone`),

  // T20B: link/unlink a bill to an inbound freight shipment + landed-cost category.
  linkShipment: async (id: string, data: BillShipmentLink): Promise<DataResponse<Bill>> =>
    apiPatch<DataResponse<Bill>>(`${API_BASE}/bills/${id}/shipment-link`, data),

  // Invoice-first AP: manually link a DRAFT bill to a PO / goods receipt(s) so it
  // relieves GRNI (the unmatched-case fallback for the auto-match at ingestion).
  linkPurchaseOrder: async (id: string, goodsReceiptIds: string[]): Promise<DataResponse<Bill>> =>
    apiPatch<DataResponse<Bill>>(`${API_BASE}/bills/${id}/po-link`, { goods_receipt_ids: goodsReceiptIds }),

  // P2P-1: build a draft bill from one confirmed goods receipt (GR "Create Bill" CTA).
  fromReceipt: async (goodsReceiptId: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/from-receipt`, { goods_receipt_id: goodsReceiptId }),

  // P2P-1: assemble one draft bill from several confirmed, unbilled receipts.
  fromReceipts: async (goodsReceiptIds: string[]): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/from-receipts`, { goods_receipt_ids: goodsReceiptIds }),

  // P2P-1: confirmed receipts for a vendor not yet on any bill (picker source).
  unbilledReceipts: async (
    vendorId: string,
    companyId?: string,
  ): Promise<{ items: UnbilledReceiptSummary[] }> =>
    apiGet<{ items: UnbilledReceiptSummary[] }>(
      `${API_BASE}/unbilled-receipts${buildParams({ vendor_id: vendorId, company_id: companyId })}`,
    ),
};

// ============== Landed Cost Categories (T20B) ==============

export const landedCostCategoryApi = {
  list: async (params?: { is_active?: boolean }): Promise<{ items: LandedCostCategory[] }> =>
    apiGet<{ items: LandedCostCategory[] }>(`${API_BASE}/landed-cost-categories${buildParams(params)}`),
};

// ============== Tax Rates ==============

export const taxRateApi = {
  list: async (params?: ListTaxRatesParams): Promise<PaginatedResponse<TaxRate>> =>
    apiGet<PaginatedResponse<TaxRate>>(`${API_BASE}/tax-rates${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<TaxRate>> =>
    apiGet<DataResponse<TaxRate>>(`${API_BASE}/tax-rates/${id}`),

  create: async (data: TaxRateCreate): Promise<DataResponse<TaxRate>> =>
    apiPost<DataResponse<TaxRate>>(`${API_BASE}/tax-rates`, data),

  update: async (id: string, data: TaxRateUpdate): Promise<DataResponse<TaxRate>> =>
    apiPut<DataResponse<TaxRate>>(`${API_BASE}/tax-rates/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/tax-rates/${id}`);
  },

  initializeDefaults: async (): Promise<DataResponse<TaxRate[]>> =>
    apiPost<DataResponse<TaxRate[]>>(`${API_BASE}/tax-rates/initialize-defaults`),
};

// ============== Tax Determination Rules ==============

export const taxDeterminationApi = {
  list: async (params?: ListTaxDeterminationRulesParams): Promise<{ data: TaxDeterminationRule[] }> =>
    apiGet<{ data: TaxDeterminationRule[] }>(`${API_BASE}/tax-determination-rules${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<TaxDeterminationRule>> =>
    apiGet<DataResponse<TaxDeterminationRule>>(`${API_BASE}/tax-determination-rules/${id}`),

  create: async (data: TaxDeterminationRuleCreate): Promise<DataResponse<TaxDeterminationRule>> =>
    apiPost<DataResponse<TaxDeterminationRule>>(`${API_BASE}/tax-determination-rules`, data),

  update: async (id: string, data: TaxDeterminationRuleUpdate): Promise<DataResponse<TaxDeterminationRule>> =>
    apiPut<DataResponse<TaxDeterminationRule>>(`${API_BASE}/tax-determination-rules/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/tax-determination-rules/${id}`);
  },

  resolve: async (data: TaxDeterminationResolveRequest): Promise<DataResponse<TaxDeterminationResult>> =>
    apiPost<DataResponse<TaxDeterminationResult>>(`${API_BASE}/tax-determination/resolve`, data),
};

// ============== Bank Accounts ==============

export const bankAccountApi = {
  list: async (params?: ListBankAccountsParams): Promise<PaginatedResponse<BankAccount>> =>
    apiGet<PaginatedResponse<BankAccount>>(`${API_BASE}/bank-accounts${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<BankAccount>> =>
    apiGet<DataResponse<BankAccount>>(`${API_BASE}/bank-accounts/${id}`),

  create: async (data: BankAccountCreateRequest): Promise<DataResponse<BankAccount>> =>
    apiPost<DataResponse<BankAccount>>(`${API_BASE}/bank-accounts`, data),

  update: async (id: string, data: BankAccountUpdateRequest): Promise<DataResponse<BankAccount>> =>
    apiPut<DataResponse<BankAccount>>(`${API_BASE}/bank-accounts/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/bank-accounts/${id}`);
  },
};

// ============== Reimbursement payers (owner/personal & third-party cards) ==============

export const reimbursementApi = {
  listPayerBalances: async (
    params?: ListPayerBalancesParams,
  ): Promise<DataResponse<PayerBalance[]>> =>
    apiGet<DataResponse<PayerBalance[]>>(`${API_BASE}/reimbursements/payers${buildParams(params)}`),

  listPayerTransactions: async (
    payerEntityTable: PayerEntityTable,
    payerEntityId: string,
    params?: ListPayerTransactionsParams,
  ): Promise<PayerTransactionsResponse> =>
    apiGet<PayerTransactionsResponse>(
      `${API_BASE}/reimbursements/payers/${payerEntityTable}/${payerEntityId}/transactions${buildParams(params)}`,
    ),

  clear: async (
    data: ReimbursementClearRequest,
  ): Promise<DataResponse<PayerReimbursementLedgerEntry>> =>
    apiPost<DataResponse<PayerReimbursementLedgerEntry>>(`${API_BASE}/reimbursements/clear`, data),

  clearBatch: async (
    data: ReimbursementClearBatchRequest,
  ): Promise<DataResponse<PayerReimbursementLedgerEntry[]>> =>
    apiPost<DataResponse<PayerReimbursementLedgerEntry[]>>(
      `${API_BASE}/reimbursements/clear-batch`,
      data,
    ),
};

// ============== T19 — Bank Deposits / Transfers / Direct Fee / Routing ==============

const BANK_BASE = `${API_BASE}/bank`;

export const bankDepositApi = {
  list: async (params?: ListBankDepositsParams): Promise<PaginatedResponse<BankDeposit>> =>
    apiGet<PaginatedResponse<BankDeposit>>(`${BANK_BASE}/deposits${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<BankDeposit>> =>
    apiGet<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}`),

  create: async (data: BankDepositCreate): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits`, data),

  update: async (id: string, data: BankDepositUpdate): Promise<DataResponse<BankDeposit>> =>
    apiPut<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}`, data),

  // Backend returns the full parent deposit (BankDepositResponse), not the line.
  addLine: async (id: string, line: BankDepositLineCreate): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}/lines`, line),

  removeLine: async (id: string, lineId: string): Promise<void> => {
    await apiDelete(`${BANK_BASE}/deposits/${id}/lines/${lineId}`);
  },

  validate: async (id: string): Promise<DataResponse<BankDepositValidateResult>> =>
    apiPost<DataResponse<BankDepositValidateResult>>(`${BANK_BASE}/deposits/${id}/validate`),

  post: async (id: string, data: BankDepositPostRequest): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}/post`, data),

  void: async (id: string, data: BankDepositVoidRequest): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}/void`, data),

  pendingReceipts: async (params?: ListPendingReceiptsParams): Promise<DataResponse<PendingReceipt[]>> =>
    apiGet<DataResponse<PendingReceipt[]>>(`${BANK_BASE}/pending-receipts${buildParams(params)}`),

  defaultRouting: async (payment_method_kind: string): Promise<DataResponse<DepositRoutingDefault>> =>
    apiGet<DataResponse<DepositRoutingDefault>>(
      `${BANK_BASE}/routing/default${buildParams({ payment_method_kind })}`,
    ),
};

export const bankTransferApi = {
  list: async (params?: ListBankTransfersParams): Promise<PaginatedResponse<BankTransfer>> =>
    apiGet<PaginatedResponse<BankTransfer>>(`${BANK_BASE}/transfers${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<BankTransfer>> =>
    apiGet<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers/${id}`),

  create: async (data: BankTransferCreate): Promise<DataResponse<BankTransfer>> =>
    apiPost<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers`, data),

  post: async (id: string): Promise<DataResponse<BankTransfer>> =>
    apiPost<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers/${id}/post`),

  void: async (id: string, data: BankTransferVoidRequest): Promise<DataResponse<BankTransfer>> =>
    apiPost<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers/${id}/void`, data),
};

export const bankFeeApi = {
  post: async (data: BankFeePostRequest): Promise<DataResponse<unknown>> =>
    apiPost<DataResponse<unknown>>(`${BANK_BASE}/post-fee`, data),

  confirmDepositMatch: async (data: ConfirmDepositMatchRequest): Promise<DataResponse<unknown>> =>
    apiPost<DataResponse<unknown>>(`${BANK_BASE}/confirm-deposit-match`, data),
};

// ============== Recurring Templates ==============

export const recurringApi = {
  list: async (params?: ListRecurringParams): Promise<PaginatedResponse<RecurringTemplate>> =>
    apiGet<PaginatedResponse<RecurringTemplate>>(`${API_BASE}/recurring${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<RecurringTemplate>> =>
    apiGet<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}`),

  create: async (data: CreateRecurringFromInvoice): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring`, data),

  createFromBill: async (
    data: CreateRecurringFromBill,
  ): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/from-bill`, data),

  update: async (id: string, data: UpdateRecurring): Promise<DataResponse<RecurringTemplate>> =>
    apiPut<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}`, data),

  // Generate this template's next document immediately (off-schedule). Returns the
  // updated template plus a reference to the created document (null if the current
  // period was already generated).
  generateNow: async (
    id: string,
  ): Promise<DataResponse<RecurringTemplate> & { generated: { id: string; type: string } | null }> =>
    apiPost<DataResponse<RecurringTemplate> & { generated: { id: string; type: string } | null }>(
      `${API_BASE}/recurring/${id}/generate-now`,
    ),

  pause: async (id: string): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}/pause`),

  resume: async (id: string): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}/resume`),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/recurring/${id}`);
  },
};
